import React from 'react';
import { Trans } from '@lingui/react/macro';
import { BarChart3, Clock, MessageSquare, Gauge, Star } from 'lucide-react';
import { MetricWithHoverDetails, DurationWithHoverDetails } from './Analysis';

interface SidebarMetricsProps {
  analysisData: any;
  isAnalyzing?: boolean;
}

export function SidebarMetrics({ analysisData, isAnalyzing }: SidebarMetricsProps) {
  // Helper values for summary
  const fileDurationSeconds: number | null =
    (analysisData as any)?.videoDurationSeconds ?? 
    (analysisData as any)?.files?.find((f: any) => f.duration)?.duration ?? 
    null;
  const transcriptionText: string | null =
    (analysisData as any)?.transcription ?? null;
  const wordsInTranscript = transcriptionText
    ? transcriptionText.trim().split(/\s+/).length
    : null;
  const wordsPerMinute =
    fileDurationSeconds && wordsInTranscript
      ? Math.round((wordsInTranscript / Math.max(fileDurationSeconds, 1)) * 60)
      : null;

  // Context for explanations
  const analysisContext = {
    duration: fileDurationSeconds || 0,
    wordsPerMinute: wordsPerMinute || 0,
    intent: (analysisData as any)?.intent || 'general',
    fillerCount: (analysisData as any)?.fillerWords?.length || 0,
  };

  if (isAnalyzing) {
    return (
      <div className="mb-4 p-3 rounded-lg border border-brand-dark-blue/20 bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5">
        <div className="text-sm font-semibold text-brand-dark-blue mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <Trans>Speech Metrics</Trans>
        </div>
        <div className="text-center py-4">
          <div className="text-xs text-brand-dark-blue/50"><Trans>Analyzing...</Trans></div>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return null;
  }

  return (
    <div className="mt-4 p-3 rounded-lg border border-brand-dark-blue/20 bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5">
      <div className="text-sm font-semibold text-brand-dark-blue mb-3 flex items-center gap-2">
        <BarChart3 className="h-4 w-4" />
         <Trans>Speech Metrics</Trans>
      </div>
      
      <div className="space-y-3">
        {/* Duration */}
        {fileDurationSeconds && (
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3 text-brand-dark-blue/70" />
            <DurationWithHoverDetails duration={fileDurationSeconds} compact />
          </div>
        )}

        {/* Words per Minute */}
        {wordsPerMinute && (
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3 w-3 text-brand-dark-blue/70" />
            <MetricWithHoverDetails
              title="Words/min"
              value={wordsPerMinute}
              metric="wordsPerMinute"
              context={analysisContext}
              compact
            />
          </div>
        )}

        {/* Filler Words */}
        <div className="flex items-center gap-2">
          <Gauge className="h-3 w-3 text-brand-dark-blue/70" />
          <MetricWithHoverDetails
            title="Filler Words"
            value={(analysisData as any)?.fillerWords?.length ?? 0}
            metric="fillerWords"
            context={analysisContext}
            compact
          />
        </div>

        {/* Sentiment */}
        {(analysisData as any)?.sentiment && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand-gold/70" />
            <MetricWithHoverDetails
              title="Sentiment"
              value={(analysisData as any)?.sentiment ?? '—'}
              metric="sentiment"
              context={analysisContext}
              compact
            />
          </div>
        )}

        {/* Pace */}
        {(analysisData as any)?.speechPace && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand-success/70" />
            <MetricWithHoverDetails
              title="Pace"
              value={(analysisData as any)?.speechPace ?? '—'}
              metric="pace"
              context={analysisContext}
              compact
            />
          </div>
        )}

        {/* Clarity */}
        {(analysisData as any)?.clarity && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand-dark-blue/70" />
            <MetricWithHoverDetails
              title="Clarity"
              value={(analysisData as any)?.clarity ?? '—'}
              metric="clarity"
              context={analysisContext}
              compact
            />
          </div>
        )}
      </div>

      {/* Key Points Detected */}
      {(analysisData as any)?.keyPointsDetected?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-brand-dark-blue/10">
          <div className="text-xs font-semibold text-brand-dark-blue mb-2 flex items-center gap-2">
            <Star className="h-3 w-3 text-brand-gold" />
            <Trans>Key Points</Trans> ({(analysisData as any).keyPointsDetected.length})
          </div>
          <div className="space-y-1  overflow-y-auto">
            {(analysisData as any).keyPointsDetected.map((point: string, index: number) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-1.5 shrink-0" />
                <span className="text-xs text-brand-dark-blue/80 leading-relaxed">{point}</span>
              </div>
            ))}
            {/* {(analysisData as any).keyPointsDetected.length > 3 && (
              <div className="text-xs text-brand-dark-blue/60 italic">
                +{(analysisData as any).keyPointsDetected.length - 3} more points
              </div>
            )} */}
          </div>
        </div>
      )}
    </div>
  );
}
